import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, Send, FileText, Download, RefreshCw,
  ChevronRight, Copy,
} from "lucide-react";
import { apiClient, formatErr, getToken, API } from "@/lib/api";

const IVA_OPTIONS = [
  { value: "22", label: "IVA 22%" },
  { value: "0", label: "IVA 0%" },
  { value: "esente", label: "IVA esente" },
];

const PREV_STATUS = {
  bozza: { label: "Bozza", cls: "bg-zinc-200 text-zinc-800" },
  inviato: { label: "Inviato", cls: "bg-emerald-500 text-white" },
  accettato: { label: "Accettato", cls: "bg-sky-500 text-white" },
  rifiutato: { label: "Rifiutato", cls: "bg-red-500 text-white" },
  scaduto: { label: "Scaduto", cls: "bg-zinc-400 text-white" },
};

const fmtEur = (v) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
};

export default function PreventiviTab({ quote }) {
  const [preventivi, setPreventivi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, {} = new draft

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/admin/quotes/${quote.id}/preventivi`);
      setPreventivi(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [quote.id]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditing({
      id: null,
      righe: [
        { descrizione: quote.tipo_ricambio || "", qty: 1, prezzo_unitario: 0 },
      ],
      iva_type: "22",
      note: "",
    });
  };

  const startEdit = (p) => {
    setEditing({
      id: p.id,
      righe: p.righe.map((r) => ({ ...r })),
      iva_type: p.iva_type,
      note: p.note || "",
    });
  };

  const startDuplicate = (p) => {
    setEditing({
      id: null,
      righe: p.righe.map((r) => ({ ...r })),
      iva_type: p.iva_type,
      note: p.note || "",
    });
    toast.info("Bozza duplicata — modifica e salva");
  };

  if (editing) {
    return (
      <PreventivoEditor
        quote={quote}
        initial={editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-600">
          {preventivi.length === 0
            ? "Nessun preventivo ancora creato per questa richiesta."
            : `${preventivi.length} preventiv${preventivi.length === 1 ? "o" : "i"} per questa richiesta`}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="prev-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 border border-zinc-300 hover:border-zinc-900 text-xs uppercase tracking-widest font-mono transition-colors bg-white"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={startNew}
            data-testid="prev-new-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#E63946] hover:bg-[#c81d25] text-white font-bold uppercase tracking-widest text-xs transition-colors"
          >
            <Plus size={14} /> Nuovo Preventivo
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-[#E63946]" size={24} />
        </div>
      ) : (
        <div className="space-y-2">
          {preventivi.map((p) => (
            <PreventivoRow
              key={p.id}
              preventivo={p}
              onEdit={() => startEdit(p)}
              onDuplicate={() => startDuplicate(p)}
              onDeleted={load}
              onSent={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PreventivoRow({ preventivo: p, onEdit, onDuplicate, onDeleted, onSent }) {
  const [sending, setSending] = useState(false);
  const status = PREV_STATUS[p.status] || PREV_STATUS.bozza;
  const editable = p.status === "bozza";

  const openPdf = async () => {
    try {
      const res = await apiClient.get(`/admin/preventivi/${p.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await apiClient.get(`/admin/preventivi/${p.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Preventivo_${p.numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const sendEmail = async () => {
    if (!window.confirm(`Inviare il preventivo ${p.numero} al cliente via email?`)) return;
    setSending(true);
    try {
      await apiClient.post(`/admin/preventivi/${p.id}/send`);
      toast.success("Preventivo inviato al cliente!");
      onSent();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setSending(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare il preventivo ${p.numero}?`)) return;
    try {
      await apiClient.delete(`/admin/preventivi/${p.id}`);
      toast.success("Preventivo eliminato");
      onDeleted();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div
      data-testid={`prev-row-${p.id}`}
      className="border border-zinc-200 bg-white p-4 flex flex-wrap items-center gap-4"
    >
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-3 mb-1">
          <span className="font-mono font-bold text-zinc-900">{p.numero}</span>
          <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-mono font-bold ${status.cls}`}>
            {status.label}
          </span>
        </div>
        <div className="text-xs text-zinc-500 font-mono">
          {p.righe.length} rig{p.righe.length === 1 ? "a" : "he"} · Emesso {fmtDate(p.data_emissione)} · Scade {fmtDate(p.data_scadenza)}
        </div>
      </div>

      <div className="text-right">
        <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400">Totale</div>
        <div className="font-heading font-black text-xl text-[#E63946]">{fmtEur(p.totale)}</div>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={openPdf}
          title="Apri PDF"
          data-testid={`prev-pdf-${p.id}`}
          className="w-9 h-9 inline-flex items-center justify-center border border-zinc-300 hover:border-zinc-900 hover:text-zinc-900 text-zinc-600 transition-colors"
        >
          <FileText size={14} />
        </button>
        <button
          onClick={downloadPdf}
          title="Scarica PDF"
          className="w-9 h-9 inline-flex items-center justify-center border border-zinc-300 hover:border-zinc-900 hover:text-zinc-900 text-zinc-600 transition-colors"
        >
          <Download size={14} />
        </button>
        <button
          onClick={onDuplicate}
          title="Duplica come nuova bozza"
          className="w-9 h-9 inline-flex items-center justify-center border border-zinc-300 hover:border-zinc-900 hover:text-zinc-900 text-zinc-600 transition-colors"
        >
          <Copy size={14} />
        </button>
        {editable && (
          <>
            <button
              onClick={onEdit}
              title="Modifica"
              data-testid={`prev-edit-${p.id}`}
              className="w-9 h-9 inline-flex items-center justify-center border border-zinc-300 hover:border-zinc-900 hover:text-zinc-900 text-zinc-600 transition-colors"
            >
              <ChevronRight size={14} />
            </button>
            <button
              onClick={remove}
              title="Elimina"
              className="w-9 h-9 inline-flex items-center justify-center border border-red-200 hover:bg-red-500 hover:text-white text-red-500 transition-colors"
            >
              <Trash2 size={14} />
            </button>
          </>
        )}
        {p.status === "bozza" && (
          <button
            onClick={sendEmail}
            disabled={sending}
            data-testid={`prev-send-${p.id}`}
            className="inline-flex items-center gap-2 px-4 h-9 bg-[#E63946] hover:bg-[#c81d25] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-xs transition-colors"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Invia
          </button>
        )}
      </div>
    </div>
  );
}

function PreventivoEditor({ quote, initial, onCancel, onSaved }) {
  const [righe, setRighe] = useState(initial.righe);
  const [ivaType, setIvaType] = useState(initial.iva_type);
  const [note, setNote] = useState(initial.note);
  const [saving, setSaving] = useState(false);

  const updateRiga = (idx, field, value) => {
    setRighe(righe.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addRiga = () =>
    setRighe([...righe, { descrizione: "", qty: 1, prezzo_unitario: 0 }]);

  const removeRiga = (idx) => {
    if (righe.length === 1) {
      toast.error("Almeno una riga è obbligatoria.");
      return;
    }
    setRighe(righe.filter((_, i) => i !== idx));
  };

  const imponibile = righe.reduce(
    (sum, r) => sum + (Number(r.qty) || 0) * (Number(r.prezzo_unitario) || 0),
    0
  );
  const iva = ivaType === "22" ? imponibile * 0.22 : 0;
  const totale = imponibile + iva;

  const isNew = !initial.id;

  const save = async () => {
    for (const r of righe) {
      if (!r.descrizione.trim()) {
        toast.error("Ogni riga deve avere una descrizione.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        righe: righe.map((r) => ({
          descrizione: r.descrizione,
          qty: Number(r.qty) || 1,
          prezzo_unitario: Number(r.prezzo_unitario) || 0,
        })),
        iva_type: ivaType,
        note,
      };
      if (isNew) {
        const { data } = await apiClient.post(
          `/admin/quotes/${quote.id}/preventivi`,
          payload
        );
        toast.success(`Preventivo ${data.numero} creato come bozza`);
      } else {
        await apiClient.patch(`/admin/preventivi/${initial.id}`, payload);
        toast.success("Preventivo aggiornato");
      }
      onSaved();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setSaving(false); }
  };

  const openPreviewPdf = async () => {
    // Salva prima come bozza (crea se nuovo, aggiorna se esiste) e apri PDF
    if (isNew) {
      toast.info("Salva prima il preventivo per generare l'anteprima PDF");
      return;
    }
    try {
      const res = await apiClient.get(`/admin/preventivi/${initial.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const inputCls =
    "w-full bg-white border border-zinc-300 px-3 py-2 text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-[#E63946] transition-colors text-sm";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-[#E63946]">
            {isNew ? "Nuova Bozza Preventivo" : "Modifica Preventivo"}
          </div>
          <div className="font-heading font-black text-xl text-zinc-900">
            {quote.tipo_ricambio} — {quote.nome}
          </div>
        </div>
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-zinc-300 hover:border-zinc-900 text-xs uppercase tracking-widest font-mono bg-white transition-colors"
        >
          Annulla
        </button>
      </div>

      {/* Righe */}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">
          Voci Preventivo
        </div>
        <div className="border border-zinc-200 bg-white">
          <div className="grid grid-cols-[1fr_80px_120px_120px_40px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest font-mono text-zinc-500">
            <div>Descrizione</div>
            <div className="text-center">Qtà</div>
            <div className="text-right">Prezzo Unit.</div>
            <div className="text-right">Totale Riga</div>
            <div></div>
          </div>
          {righe.map((r, idx) => (
            <div
              key={idx}
              data-testid={`prev-riga-${idx}`}
              className="grid grid-cols-[1fr_80px_120px_120px_40px] gap-2 px-3 py-2 border-b border-zinc-100 items-center"
            >
              <input
                value={r.descrizione}
                onChange={(e) => updateRiga(idx, "descrizione", e.target.value)}
                placeholder="es. Motore completo Audi A4..."
                data-testid={`prev-desc-${idx}`}
                className={inputCls}
              />
              <input
                type="number"
                min="0"
                step="1"
                value={r.qty}
                onChange={(e) => updateRiga(idx, "qty", e.target.value)}
                data-testid={`prev-qty-${idx}`}
                className={`${inputCls} text-center`}
              />
              <input
                type="number"
                min="0"
                step="0.01"
                value={r.prezzo_unitario}
                onChange={(e) => updateRiga(idx, "prezzo_unitario", e.target.value)}
                placeholder="0,00"
                data-testid={`prev-price-${idx}`}
                className={`${inputCls} text-right`}
              />
              <div className="text-right font-mono text-sm text-zinc-900 font-bold">
                {fmtEur((Number(r.qty) || 0) * (Number(r.prezzo_unitario) || 0))}
              </div>
              <button
                onClick={() => removeRiga(idx)}
                className="w-8 h-8 inline-flex items-center justify-center text-zinc-400 hover:text-red-500 transition-colors"
                title="Rimuovi riga"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addRiga}
          data-testid="prev-add-riga"
          className="mt-2 inline-flex items-center gap-2 px-3 py-2 border border-dashed border-zinc-400 hover:border-[#E63946] hover:text-[#E63946] text-zinc-600 text-xs uppercase tracking-widest font-mono transition-colors"
        >
          <Plus size={12} /> Aggiungi Riga
        </button>
      </div>

      {/* IVA + Totali */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">
            Regime IVA
          </div>
          <div className="flex gap-2">
            {IVA_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setIvaType(opt.value)}
                data-testid={`prev-iva-${opt.value}`}
                className={`flex-1 px-3 py-2 text-xs uppercase tracking-widest font-mono font-bold transition-colors ${
                  ivaType === opt.value
                    ? "bg-[#E63946] text-white"
                    : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-zinc-50 border border-zinc-200 p-4">
          <div className="flex justify-between text-sm text-zinc-600 py-1">
            <span>Imponibile</span>
            <span className="font-mono">{fmtEur(imponibile)}</span>
          </div>
          <div className="flex justify-between text-sm text-zinc-600 py-1">
            <span>{IVA_OPTIONS.find((o) => o.value === ivaType)?.label}</span>
            <span className="font-mono">{ivaType === "esente" ? "—" : fmtEur(iva)}</span>
          </div>
          <div className="flex justify-between border-t-2 border-zinc-900 mt-2 pt-2">
            <span className="font-heading font-black text-lg text-zinc-900">TOTALE</span>
            <span className="font-heading font-black text-xl text-[#E63946]">{fmtEur(totale)}</span>
          </div>
        </div>
      </div>

      {/* Note */}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">
          Note (facoltative)
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="prev-note"
          rows={4}
          placeholder="es. Motore revisionato con banco prova. Consegna 5-7 gg. Pagamento via bonifico."
          className={`${inputCls} resize-none`}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-200">
        <button
          onClick={save}
          disabled={saving}
          data-testid="prev-save"
          className="inline-flex items-center gap-3 px-6 py-3 bg-[#E63946] hover:bg-[#c81d25] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-xs transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          {isNew ? "Crea Bozza" : "Aggiorna Bozza"}
        </button>
        {!isNew && (
          <button
            onClick={openPreviewPdf}
            className="inline-flex items-center gap-3 px-6 py-3 border border-zinc-300 hover:border-zinc-900 text-zinc-700 hover:text-zinc-900 font-bold uppercase tracking-widest text-xs bg-white transition-colors"
          >
            <FileText size={14} />
            Anteprima PDF
          </button>
        )}
      </div>

      <div className="text-xs text-zinc-500 leading-relaxed pt-3">
        Il preventivo viene creato come <strong>bozza</strong>. Potrai modificarlo, generare
        il PDF e quando sei pronto premere &quot;Invia&quot; per spedirlo al cliente via email con
        l&apos;allegato PDF. Validità automatica di 15 giorni dalla creazione.
      </div>
    </div>
  );
}
